import type { StatusLabel } from '@natashu/shared';
import { CircleHelp, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { cx } from '../../lib/cx.ts';
import { iconByName } from './icons.ts';
import './Badge.css';

export type Tone = StatusLabel['tone'];

export interface BadgeProps {
  tone?: Tone;
  icon?: LucideIcon;
  children: ReactNode;
  size?: 'sm' | 'md';
  title?: string;
  className?: string;
}

/** Pill badge. For statuses always pass an icon — colour is never the only signal. */
export function Badge({ tone = 'neutral', icon: Icon, children, size = 'md', title, className }: BadgeProps) {
  return (
    <span className={cx('badge', `badge--${tone}`, size === 'sm' && 'badge--sm', !Icon && 'badge--no-icon', className)} title={title}>
      {Icon && <Icon size={size === 'sm' ? 12 : 14} strokeWidth={2} aria-hidden="true" />}
      <span className="badge__text">{children}</span>
    </span>
  );
}

export interface StatusPillProps {
  /** A label from the shared tables, e.g. `SEND_STATE_LABELS[state]`. */
  status: StatusLabel | null | undefined;
  fallback?: string;
  size?: 'sm' | 'md';
  /** Show the label's hint as a native tooltip. */
  showHint?: boolean;
  className?: string;
}

export function StatusPill({ status, fallback = 'ไม่ทราบสถานะ', size = 'md', showHint = true, className }: StatusPillProps) {
  if (!status) {
    return (
      <Badge tone="neutral" icon={CircleHelp} size={size} className={className}>
        {fallback}
      </Badge>
    );
  }
  return (
    <Badge tone={status.tone} icon={iconByName(status.icon)} size={size} title={showHint && status.hint ? status.hint : undefined} className={className}>
      {status.th}
    </Badge>
  );
}
